/* eslint-disable no-unused-vars */
import React, { useLayoutEffect, useState, useEffect } from "react";
import styles from "./AboutVirdio.module.css";
import { useHistory } from "react-router-dom";
import checkBrowserSupport from "../self-assesment/browserDetect";
import { AnalyticsService, BrowserBlockScreen } from "../self-assesment/SelfAssesment";
import VoiceService from "../voice-agent/VH-Myra-FrontEnd";
import { SubPageHeader } from "./SubHeader";

// ============================================================
// CONSTANTS
// ============================================================
const LOGO_URL = "https://virdiodev.s3.us-east-2.amazonaws.com/sampleVideo/LogoPNG.png";

// ============================================================
// useScrollToTop
// ============================================================
const useScrollToTop = () => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
};

// ============================================================
// Team data — photo paths match About.jsx conventions
// ============================================================
const TEAM_MEMBERS = [
  {
    name: "Arjun Rishi",
    role: "Co-Founder & CEO",
    bio: "Built Virdio Health after his own struggle with back and knee pain made it clear that recovery needed to be more accessible.",
    photo: "/aboutimg/rishi.svg",
    initials: "AR",
  },
  {
    name: "Dr. Mehul Desai",
    role: "Chief Medical Officer",
    bio: "A nationally recognized pain specialist who helps shape how Myra supports people through recovery.",
    photo: "/aboutimg/Desai.png",
    initials: "MD",
  },
  {
    name: "Nicholas Sterling",
    role: "Chief R&D Officer",
    bio: "An AI veteran who leads the technology that allows Myra to guide your movements in real-time.",
    photo: "/aboutimg/Sterling.svg",
    initials: "NS",
  },
  {
    name: "Ty Bordner",
    role: "Co-Founder & CCO",
    bio: "Decades of experience scaling health and technology companies from startup through IPO.",
    photo: "/aboutimg/bordner.svg",
    initials: "TB",
  },
  {
    name: "McKenna Lupold, DPT",
    role: "Clinical Advisor",
    bio: "A clinical advisor who helps make sure Myra's recovery approach stays grounded in physical therapy principles.",
    photo: "/aboutimg/Lupold.svg",
    initials: "ML",
  },
];

// ============================================================
// Myra capabilities data
// ============================================================
const MYRA_CAPABILITIES = [
  {
    title: "She understands you",
    desc: "Myra asks questions and learns how you move to figure out what works best for you.",
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: "She builds your plan",
    desc: "Using that information, she creates a recovery plan tailored to your body and pain.",
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
  },
  {
    title: 'She explains the "why"',
    desc: "Myra shares with you exactly why she chose each exercise so you can move with confidence.",
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: "She's always available",
    desc: "Have questions about your plan? Myra is there to answer them anytime.",
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
];

// ============================================================
// AboutVirdioPage — main export
// ============================================================
export const AboutVirdioPage = () => {
  useScrollToTop();
  const history = useHistory();
  const [showMemberBlockScreen, setShowMemberBlockScreen] = useState(false);

  // ---- Session initialization on page load ----
  useEffect(() => {
    const previousSessionId = window.trSessionId || localStorage.getItem("trSessionId");
    if (previousSessionId) {
      localStorage.removeItem("trSessionId");
      window.trSessionId = null;
    }

    const isRepeat = localStorage.getItem("isUserCameBackAgain") === "true";
    localStorage.setItem("isUserCameBackAgain", "true");

    const params = new URLSearchParams(window.location.search);
    const gclid = params.get("gclid");
    const urlVariant = params.get("variant");
    const resolvedVariant = urlVariant || localStorage.getItem("variant") || null;

    if (urlVariant) {
      localStorage.setItem("variant", urlVariant);
    }

    const isFromGoogle = !!(gclid || resolvedVariant);

    // Mark that the user visited a subpage this session. If they later navigate
    // to the landing page, HomeScreen reads (and consumes) this flag to set
    // cameToLandingPage = 1 on the landing-page session record.
    localStorage.setItem("visitedSubpageBeforeLanding", "true");

    const trackingFields = {
      variant: resolvedVariant,
      isComeFromGoogle: isFromGoogle,
      isUserCameBackAgain: isRepeat,
      visitedAbout: true,
    };

    AnalyticsService.startSession(trackingFields);
  }, []);

  const handleStartAssessment = () => {
    if (!window.trSessionId) {
      AnalyticsService.startSession({ visitedAbout: 1 });
    }
    history.replace("/problem-area");
  };

  if (showMemberBlockScreen) {
    return <BrowserBlockScreen featureType="member" />;
  }

  return (
    <div className={styles.page}>
      <SubPageHeader activePage="how-it-works" setShowMemberBlockScreen={setShowMemberBlockScreen} />

      {/* ===== HERO ===== */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <h1 className={styles.heroHeadline}>Why we built StartFreePT</h1>
          <p className={styles.heroSub}>Recovery shouldn't feel out of reach.</p>
        </div>
      </section>

      {/* ===== ORIGIN STORY ===== */}
      <section className={`${styles.aboutSection} ${styles.bgWhite}`}>
        <div className={styles.container}>
          <div className={styles.aboutBody}>
            <p>Our team started Virdio Health because we've lived the frustration of recovery. Getting started is often harder than it should be. Before StartFreePT, your options were limited: travel to a clinic, hope your employer offered a digital program, or pay hundreds out of pocket.</p>
            <p>We built StartFreePT because we believed there should be a better way to begin recovery. One that is more accessible, more affordable, and designed to fit into real life.</p>
          </div>
        </div>
      </section>

      {/* ===== HOW MYRA HELPS ===== */}
      <section className={`${styles.aboutSection} ${styles.bgGray}`}>
        <div className={styles.container}>
          <h2 className={styles.aboutSectionTitle}>How Myra helps</h2>
          <div className={styles.aboutBody} style={{ marginBottom: 0 }}>
            <p>Myra is your recovery coach, built on evidence-based research and informed by Doctors of Physical Therapy. While she is not a doctor or clinician, she is designed to help you guide your own recovery at home.</p>
          </div>

          <div className={styles.capabilityList}>
            {MYRA_CAPABILITIES.map((cap, index) => (
              <div key={index} className={styles.capabilityItem}>
                <div className={styles.capabilityIcon}>{cap.icon}</div>
                <div>
                  <p className={styles.capabilityTitle}>{cap.title}</p>
                  <p className={styles.capabilityDesc}>{cap.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA — ready to see what Myra can do ===== */}
      <section className={`${styles.aboutSection} ${styles.bgWhite}`}>
        <div className={styles.container}>
          <div className={styles.ctaCard}>
            <h2 className={styles.ctaHeading}>Ready to see what Myra can do?</h2>
            <p className={styles.ctaSubtext}>Start your free guided assessment. It takes about 10 minutes, there's nothing to download, and no signup required.</p>
            <button className={styles.btnPrimary} onClick={handleStartAssessment}>
              Start Free Assessment
            </button>
            <p className={styles.ctaReassurance}>No commitment. No credit card. Stop anytime.</p>
            <div className={styles.secondaryCtaRow}>
              <p className={styles.secondaryCtaLabel}>Want to learn more about the assessment first?</p>
              <button className={styles.btnTextLink} onClick={() => history.push("/how-it-works")}>
                How the assessment works →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TEAM ===== */}
      <section className={`${styles.aboutSection} ${styles.bgGray}`}>
        <div className={styles.container}>
          <h2 className={styles.aboutSectionTitle}>The team behind your recovery</h2>

          <div className={styles.teamGrid}>
            {TEAM_MEMBERS.map((member, index) => (
              <div key={index} className={styles.teamCard}>
                <div className={styles.teamAvatar}>
                  <img
                    src={member.photo}
                    alt={member.name}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentElement.textContent = member.initials;
                    }}
                  />
                </div>
                <div>
                  <p className={styles.teamName}>{member.name}</p>
                  <p className={styles.teamRole}>{member.role}</p>
                  <p className={styles.teamBio}>{member.bio}</p>
                </div>
              </div>
            ))}
          </div>

          <p className={styles.teamCloser}>Recovery is personal. So is the team behind it.</p>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaCard}>
            <h2 className={styles.ctaHeading}>Start your recovery today. It's free.</h2>
            <button className={styles.btnPrimary} onClick={handleStartAssessment}>
              Start Free Assessment
            </button>
            <p className={styles.ctaReassurance}>No commitment. No credit card. Stop anytime.</p>
          </div>
        </div>
      </section>

      {/* ===== DISCLAIMER ===== */}
      <div className={styles.disclaimer}>
        <div className={styles.container}>
          <p className={styles.disclaimerText}>StartFreePT provides educational guidance based on your input. It is not a substitute for medical advice, diagnosis, or treatment.</p>
        </div>
      </div>

      {/* ===== FOOTER ===== */}
      <footer className={styles.footer}>
        <div className={styles.container}>
          <p className={styles.footerText}>© 2025 Virdio Health. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
